import React from 'react';
import { Box, Card, CardContent, CircularProgress, Tooltip, Typography } from '@mui/material';
import { DISPLAY_CARRIERS } from '../../config/carriers';
import { SMS_ACTIVITY_LEGEND, SMS_ACTIVITY_ORDER } from '../../config/reminderStatuses';
import type { DashboardStats } from '../../services/dashboardService';

interface SmsByCarrierCardProps {
  smsByCarrier: DashboardStats['smsByCarrier'];
  loading: boolean;
}

export const SmsByCarrierCard: React.FC<SmsByCarrierCardProps> = ({ smsByCarrier, loading }) => (
  <Card variant="outlined" elevation={0}>
    <CardContent>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        SMS Activity by GSM Network
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {DISPLAY_CARRIERS.map((carrier) => {
            const data = smsByCarrier[carrier.key];
            // Summed over whatever statuses came back, so a status the backend adds later still counts.
            const total = Object.values(data).reduce((sum, n) => sum + (n ?? 0), 0);
            const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
            return (
              <Box key={carrier.key}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: carrier.color }} />
                    <Typography variant="body2" fontWeight={500}>{carrier.label}</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    {total.toLocaleString()} total
                  </Typography>
                </Box>
                {/* Legend and bar come from the same ordered list, so every swatch has a segment. */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 0.5, mb: 0.5 }}>
                  {SMS_ACTIVITY_ORDER.map((statusKey) => {
                    const entry = SMS_ACTIVITY_LEGEND[statusKey];
                    // Defaulted: mid-rollout this build can hit an older API missing a status,
                    // and reading it raw blanked the whole dashboard.
                    const count = data[statusKey] ?? 0;
                    return (
                      <Tooltip key={statusKey} title={entry.description}>
                        {/* tabIndex lets keyboard and touch users open the tooltip. */}
                        <Box tabIndex={0} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color, flexShrink: 0 }} />
                          <Typography variant="caption" color="text.secondary">
                            {entry.label}: <strong>{count.toLocaleString()}</strong>
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', width: '100%', height: 8, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}>
                  {SMS_ACTIVITY_ORDER.map((statusKey) => (
                    <Box
                      key={statusKey}
                      sx={{
                        width: `${pct(data[statusKey] ?? 0)}%`,
                        bgcolor: SMS_ACTIVITY_LEGEND[statusKey].color,
                        transition: 'width 0.5s ease',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </CardContent>
  </Card>
);
